import {
  Archive,
  Brain,
  Calendar,
  CalendarCheck,
  ChevronRight,
  Ellipsis,
  FileText,
  Grid2x2,
  Inbox,
  Library,
  ListChecks,
  Mountain,
  Package,
  Settings,
  ShieldCheck,
  Sun,
  Target,
  Trash2,
  Users,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { type Area, areas, type LocalRecord, type Note } from '../../shared/model';

import type { Appearance } from '../lib/appearance';
import { desktop } from '../lib/desktop';
import { type Page, pagePaths, RouteLink } from '../lib/routes';
import { production } from '../lib/runtime';
import { TrailScene } from './TrailScene';
import { Button, IconTile, PageHeader, type Tone } from './ui';

/**
 * Where everything lives, grouped as the phone groups it: four tabs of daily
 * work, the library's five areas under Notes, and the rest behind More. The
 * symbols and tones are the phone's, drawn with their nearest Lucide glyphs.
 */

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

type Destination = { page: Page; title: string; detail: string; icon: Icon; tone: Tone };

export const areaDestinations: Record<Area, Destination> = {
  Inbox: { page: 'Inbox', title: 'Inbox', detail: 'Not yet filed', icon: Inbox, tone: 'info' },
  Projects: {
    page: 'Projects',
    title: 'Projects',
    detail: 'Has an end in sight',
    icon: Package,
    tone: 'focus',
  },
  Areas: {
    page: 'Areas',
    title: 'Areas',
    detail: 'Ongoing responsibility',
    icon: Grid2x2,
    tone: 'success',
  },
  Resources: {
    page: 'Resources',
    title: 'Resources',
    detail: 'Kept to hand',
    icon: Library,
    tone: 'attention',
  },
  Archive: {
    page: 'Archive',
    title: 'Archive',
    detail: 'Finished and put away',
    icon: Archive,
    tone: 'neutral',
  },
};

export const moreDestinations: Destination[] = [
  {
    page: 'Vault',
    title: 'Vault',
    detail: 'Passwords, cards and private notes',
    icon: ShieldCheck,
    tone: 'neutral',
  },
  {
    page: 'Goals',
    title: 'Goals',
    detail: 'Outcomes worth measuring',
    icon: Target,
    tone: 'focus',
  },
  {
    page: 'Reviews',
    title: 'Weekly review',
    detail: 'A look back, never a backlog',
    icon: CalendarCheck,
    tone: 'success',
  },
  {
    page: 'People',
    title: 'People',
    detail: 'Who is waiting, and on what',
    icon: Users,
    tone: 'attention',
  },
  {
    page: 'Learning',
    title: 'Learning',
    detail: 'Today’s cards to review',
    icon: Brain,
    tone: 'info',
  },
  {
    page: 'Trash',
    title: 'Trash',
    detail: 'Everything put aside, to restore or clear',
    icon: Trash2,
    tone: 'danger',
  },
  {
    page: 'Lab',
    title: 'Settings & recovery',
    detail: 'Backups, calendar file and restore',
    icon: Settings,
    tone: 'neutral',
  },
];

export const tabDestinations: {
  page: Page;
  title: string;
  icon: Icon;
  tone: Tone;
  owns: string[];
}[] = [
  { page: 'Today', title: 'Today', icon: Sun, tone: 'priority', owns: ['Today'] },
  { page: 'Tasks', title: 'Tasks', icon: ListChecks, tone: 'info', owns: ['Tasks', 'Task'] },
  { page: 'Calendar', title: 'Calendar', icon: Calendar, tone: 'info', owns: ['Calendar'] },
  { page: 'Notes', title: 'Notes', icon: FileText, tone: 'focus', owns: ['Notes', ...areas] },
  {
    page: 'More',
    title: 'More',
    icon: Ellipsis,
    tone: 'neutral',
    owns: ['More', ...moreDestinations.map(d => d.page)],
  },
];

function NavItem({
  destination,
  selected,
  count,
}: {
  destination: Pick<Destination, 'page' | 'title' | 'icon' | 'tone'>;
  selected: boolean;
  count?: number;
}) {
  return (
    <RouteLink
      className={selected ? 'nav-item selected' : 'nav-item'}
      aria-current={selected ? 'page' : undefined}
      href={pagePaths[destination.page]}
    >
      <IconTile icon={destination.icon} tone={destination.tone} size="small" />
      <span className="nav-title">{destination.title}</span>
      {count !== undefined && count > 0 && <span className="nav-count">{count}</span>}
    </RouteLink>
  );
}

/** The desktop's navigation: a light column on the canvas, grouped as the phone's tabs. */
export function Sidebar({
  page,
  today,
  notes,
  onToggleTheme,
  dark,
  onSignOut,
}: {
  page: string;
  today: number;
  notes: LocalRecord[];
  onToggleTheme: () => void;
  dark: boolean;
  onSignOut: () => void;
}) {
  const count = (area: Area) => notes.filter(n => (n.value as Note).area === area).length;

  return (
    <aside className="sidebar">
      {desktop ? (
        // The Mac app names itself in the menu bar; the top of the column holds
        // the traffic lights and moves the window, as the title bar beside it does.
        <div className="sidebar-titlebar">
          {!production && <span className="prototype-badge">LAB</span>}
        </div>
      ) : (
        <div className="brand">
          <Mountain aria-hidden="true" />
          <span>Cairn</span>
          {!production && <span className="prototype-badge">LAB</span>}
        </div>
      )}
      <nav aria-label="Main navigation">
        {tabDestinations.slice(0, 3).map(tab => (
          <NavItem
            key={tab.page}
            destination={tab}
            selected={tab.owns.includes(page)}
            count={tab.page === 'Today' ? today : undefined}
          />
        ))}
        <div className="nav-heading">Notes</div>
        {areas.map(area => (
          <NavItem
            key={area}
            destination={areaDestinations[area]}
            selected={page === area}
            count={count(area)}
          />
        ))}
        <div className="nav-heading">More</div>
        {moreDestinations.map(destination => (
          <NavItem
            key={destination.page}
            destination={destination}
            selected={page === destination.page}
          />
        ))}
      </nav>
      <div className="sidebar-bottom">
        <Button variant="ghost" onClick={onToggleTheme}>
          {dark ? 'Light appearance' : 'Dark appearance'}
        </Button>
        <Button variant="ghost" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </aside>
  );
}

/** The phone's five tabs, floating over the page on a phone-width window. */
export function TabBar({ page, today }: { page: string; today: number }) {
  return (
    <nav className="tab-bar" aria-label="Tabs">
      {tabDestinations.map(tab => {
        const selected = tab.owns.includes(page);
        const Icon = tab.icon;

        return (
          <RouteLink
            key={tab.page}
            className={selected ? 'tab selected' : 'tab'}
            aria-current={selected ? 'page' : undefined}
            href={pagePaths[tab.page]}
          >
            <span className="tab-icon">
              <Icon aria-hidden="true" />
              {tab.page === 'Today' && today > 0 && <span className="tab-badge">{today}</span>}
            </span>
            <span>{tab.title}</span>
          </RouteLink>
        );
      })}
    </nav>
  );
}

/** A row that opens a page: its tile, a semibold title, a caption and a chevron. */
export function DestinationRow({
  destination,
  count,
}: {
  destination: Destination;
  count?: number;
}) {
  return (
    <RouteLink className="destination-row" href={pagePaths[destination.page]}>
      <IconTile icon={destination.icon} tone={destination.tone} />
      <span className="destination-text">
        <strong>{destination.title}</strong>
        <small>{destination.detail}</small>
      </span>
      {count !== undefined && <span className="destination-count">{count}</span>}
      <ChevronRight className="destination-chevron" aria-hidden="true" />
    </RouteLink>
  );
}

/** The PARA library's areas, as the Notes tab opens on the phone. */
export function NotesPage({ notes }: { notes: LocalRecord[] }) {
  return (
    <main className="productivity-page destinations-page">
      <PageHeader title="Notes" />
      <section className="destination-group" aria-label="Library areas">
        {areas.map(area => (
          <DestinationRow
            key={area}
            destination={areaDestinations[area]}
            count={notes.filter(n => (n.value as Note).area === area).length}
          />
        ))}
      </section>
      <p className="destination-footer">
        Projects have an end. Areas do not. Resources are what you keep to hand, and Archive is what
        is finished.
      </p>
    </main>
  );
}

const appearanceNames: Record<Appearance, string> = {
  system: 'Match system',
  light: 'Light',
  dark: 'Dark',
};

function AppearanceScene({ theme }: { theme: 'light' | 'dark' }) {
  return (
    <span className={`appearance-scene theme-${theme}`}>
      <TrailScene sky={theme === 'dark' ? 'night' : 'day'} height={70} />
      <span className="appearance-card">
        <i />
      </span>
    </span>
  );
}

/** Match system, Light and Dark, each drawn as the app would look. */
export function AppearancePicker({
  appearance,
  onChange,
}: {
  appearance: Appearance;
  onChange: (value: Appearance) => void;
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: the shared fieldset rule in ui.css draws a bordered panel; this group sits inside one already
    <div className="appearance-picker" role="group" aria-label="Appearance">
      {(['system', 'light', 'dark'] as const).map(option => (
        <button
          key={option}
          type="button"
          data-ui="specialized"
          className="appearance-swatch"
          aria-pressed={appearance === option}
          onClick={() => onChange(option)}
        >
          <span className="appearance-preview">
            {option === 'system' ? (
              <>
                <AppearanceScene theme="light" />
                <span className="appearance-split">
                  <AppearanceScene theme="dark" />
                </span>
              </>
            ) : (
              <AppearanceScene theme={option} />
            )}
          </span>
          <span className="appearance-name">{appearanceNames[option]}</span>
        </button>
      ))}
    </div>
  );
}

/** Everything that is not a tab, as the phone's More lists it. */
export function MorePage({
  appearance,
  onAppearance,
  onSync,
  onSignOut,
  records,
  pending,
  lastSync,
  email,
}: {
  appearance: Appearance;
  onAppearance: (value: Appearance) => void;
  onSync: () => void;
  onSignOut: () => void;
  records: LocalRecord[];
  pending: number;
  lastSync?: string | null;
  email?: string;
}) {
  const tasks = records.filter(
    r => r.kind === 'task' && !(r.value as { trashed?: boolean }).trashed,
  );

  return (
    <main className="productivity-page destinations-page">
      <PageHeader title="More" />
      <section className="destination-group" aria-label="Vault">
        <DestinationRow destination={moreDestinations[0]} />
      </section>
      <p className="destination-footer">
        Part of this workspace, behind the same sign-in. Use test credentials until the security
        review is recorded.
      </p>
      <section className="destination-group" aria-label="Planning">
        {moreDestinations.slice(1, 5).map(destination => (
          <DestinationRow key={destination.page} destination={destination} />
        ))}
      </section>
      <div className="section-title">
        <h2>Appearance</h2>
      </div>
      <section className="destination-group appearance-group">
        <AppearancePicker appearance={appearance} onChange={onAppearance} />
      </section>
      <p className="destination-footer">Kept on this device. The phone keeps its own.</p>
      <div className="section-title">
        <h2>Workspace</h2>
      </div>
      <dl className="destination-group destination-facts">
        <div>
          <dt>Tasks</dt>
          <dd>{tasks.length}</dd>
        </div>
        <div>
          <dt>Pending</dt>
          <dd>{pending}</dd>
        </div>
        {lastSync && (
          <div>
            <dt>Last sync</dt>
            <dd>{new Date(lastSync).toLocaleTimeString()}</dd>
          </div>
        )}
        {email && (
          <div>
            <dt>Signed in as</dt>
            <dd>{email}</dd>
          </div>
        )}
      </dl>
      <section className="destination-group" aria-label="Settings">
        {moreDestinations.slice(5).map(destination => (
          <DestinationRow key={destination.page} destination={destination} />
        ))}
      </section>
      <div className="button-row wrap destination-actions">
        <Button onClick={onSync}>Sync now</Button>
        <Button variant="danger" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </main>
  );
}
